import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppLayout from '../../components/AppLayout';
import {
  getMarketDates,
  marketCountByDate,
  marketCountByMonth,
  marketDeleteByDate,
  marketDeleteByMonth,
  getStrategyDbDates,
  strategyCountByDate,
  strategyCountByMonth,
  strategyDeleteByDate,
  strategyDeleteByMonth,
} from '../../api/adminService';
import './DBManagement.css';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const MONTH_NAMES_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];

/**
 * Build a hierarchical catalog from flat dates array.
 * isIso=true  → dates are ISO strings  (market data)
 * isIso=false → dates are DD-MMM-YYYY strings (strategy data, e.g. "15-May-2026")
 * Returns { catalog: { year: { month: [{ day, value }] } }, years: number[] }
 */
const STRAT_MONTH_MAP = { jan:1, feb:2, mar:3, apr:4, may:5, jun:6, jul:7, aug:8, sep:9, oct:10, nov:11, dec:12 };

function buildDateCatalog(dates, isIso) {
  const seen = new Map(); // key "YYYY-MM-DD" → { year, month, day, value }
  dates.forEach((d) => {
    let year, month, day, value;
    if (isIso) {
      const obj = new Date(d);
      if (isNaN(obj)) return;
      year  = obj.getUTCFullYear();
      month = obj.getUTCMonth() + 1;
      day   = obj.getUTCDate();
      value = d;
    } else if (typeof d === 'string') {
      const parts = d.split('-');
      if (parts.length !== 3) return;
      if (isNaN(Number(parts[1]))) {
        // DD-MMM-YYYY (e.g. "15-May-2026") — primary format from chartink sync & CSV
        day   = Number(parts[0]);
        month = STRAT_MONTH_MAP[parts[1].toLowerCase()] ?? 0;
        year  = Number(parts[2]);
        if (!month || isNaN(day) || isNaN(year)) return;
      } else {
        // DD-MM-YY legacy format
        const [dd, mm, yy] = parts.map(Number);
        day = dd; month = mm; year = 2000 + yy;
      }
      value = d;
    } else return;

    const key = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    if (!seen.has(key)) seen.set(key, { year, month, day, value });
  });

  const catalog = {};
  for (const { year, month, day, value } of seen.values()) {
    if (!catalog[year]) catalog[year] = {};
    if (!catalog[year][month]) catalog[year][month] = [];
    catalog[year][month].push({ day, value });
  }
  // sort days descending within each month
  Object.values(catalog).forEach((months) =>
    Object.values(months).forEach((days) => days.sort((a, b) => b.day - a.day))
  );
  const years = Object.keys(catalog).map(Number).sort((a, b) => b - a);
  return { catalog, years };
}

/** Human-readable date label from a value */
function humanDate(value, isIso) {
  if (!value) return '';
  if (isIso) {
    const d = new Date(value);
    if (isNaN(d)) return value;
    return `${String(d.getUTCDate()).padStart(2,'0')} ${MONTH_NAMES_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
  }
  // Strategy date: DD-MMM-YYYY (e.g. "15-May-2026") or legacy DD-MM-YY
  const parts = value.split('-');
  if (parts.length !== 3) return value;
  if (isNaN(Number(parts[1]))) {
    // DD-MMM-YYYY → "15 May 2026"
    return `${parts[0]} ${parts[1]} ${parts[2]}`;
  }
  // DD-MM-YY legacy
  const [dd, mm, yy] = parts.map(Number);
  return `${String(dd).padStart(2,'0')} ${MONTH_NAMES_LONG[mm - 1]} ${2000 + yy}`;
}

function formatMonthLabel(ym) {
  if (!ym) return '';
  const [year, month] = ym.split('-');
  return `${MONTH_NAMES_LONG[parseInt(month) - 1]} ${year}`;
}

/* ─────────────────────────────────────────────
   Cascading Date Picker  (Year → Month → Day)
───────────────────────────────────────────── */
function CascadingDatePicker({ catalog, years, onChange }) {
  const [selYear,  setSelYear]  = useState('');
  const [selMonth, setSelMonth] = useState('');
  const [selDay,   setSelDay]   = useState('');

  const availMonths = selYear ? Object.keys(catalog[selYear] || {}).map(Number).sort((a,b) => b - a) : [];
  const availDays   = (selYear && selMonth) ? (catalog[selYear]?.[selMonth] || []) : [];

  const handleYear = (y) => { setSelYear(y); setSelMonth(''); setSelDay(''); onChange(null); };
  const handleMonth = (m) => { setSelMonth(m); setSelDay(''); onChange(null); };
  const handleDay = (val) => {
    const d = availDays.find((x) => x.value === val);
    setSelDay(val);
    onChange(d?.value || null);
  };

  return (
    <div className="dbm-cascade-picker">
      <div className="dbm-field">
        <label>Year</label>
        <select value={selYear} onChange={(e) => handleYear(e.target.value)}>
          <option value="">— Year —</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="dbm-field">
        <label>Month</label>
        <select value={selMonth} onChange={(e) => handleMonth(e.target.value)} disabled={!selYear}>
          <option value="">— Month —</option>
          {availMonths.map((m) => <option key={m} value={m}>{MONTH_NAMES_LONG[m - 1]}</option>)}
        </select>
      </div>
      <div className="dbm-field">
        <label>Day</label>
        <select value={selDay} onChange={(e) => handleDay(e.target.value)} disabled={!selMonth}>
          <option value="">— Day —</option>
          {availDays.map((d) => (
            <option key={d.value} value={d.value}>{String(d.day).padStart(2,'0')}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Cascading Month Picker  (Year → Month)
───────────────────────────────────────────── */
function CascadingMonthPicker({ catalog, years, onChange }) {
  const [selYear,  setSelYear]  = useState('');
  const [selMonth, setSelMonth] = useState('');

  const availMonths = selYear ? Object.keys(catalog[selYear] || {}).map(Number).sort((a,b) => b - a) : [];

  const handleYear = (y) => { setSelYear(y); setSelMonth(''); onChange(null); };
  const handleMonth = (m) => {
    setSelMonth(m);
    onChange(m ? `${selYear}-${String(m).padStart(2,'0')}` : null);
  };

  return (
    <div className="dbm-cascade-picker">
      <div className="dbm-field">
        <label>Year</label>
        <select value={selYear} onChange={(e) => handleYear(e.target.value)}>
          <option value="">— Year —</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div className="dbm-field">
        <label>Month</label>
        <select value={selMonth} onChange={(e) => handleMonth(e.target.value)} disabled={!selYear}>
          <option value="">— Month —</option>
          {availMonths.map((m) => (
            <option key={m} value={m}>{MONTH_NAMES_LONG[m - 1]} {selYear}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Double-Confirmation Modal
───────────────────────────────────────────── */
function ConfirmModal({ config, onClose }) {
  const [step, setStep]         = useState(1); // 1 = password, 2 = type DELETE
  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  if (!config) return null;

  const handleStep1 = () => {
    if (!password.trim()) { setError('Please enter your password.'); return; }
    setError('');
    setStep(2);
  };

  const handleConfirm = async () => {
    if (confirmText.trim() !== 'DELETE') {
      setError('You must type DELETE exactly to confirm.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await config.onConfirm(password);
      onClose({ success: true });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.response?.data?.message || err.message || 'Operation failed.';
      setError(msg);
      setLoading(false);
      setStep(1);
      setPassword('');
      setConfirmText('');
    }
  };

  return (
    <div className="dbm-overlay" onClick={(e) => e.target === e.currentTarget && onClose(null)}>
      <div className="dbm-modal">
        <div className="dbm-modal-header danger">
          <span className="dbm-modal-icon">⚠️</span>
          <h3>Delete Confirmation — Step {step}/2</h3>
        </div>

        <div className="dbm-modal-body">
          <div className="dbm-delete-target">
            <span className="dbm-target-label">{config.sectionLabel}</span>
            <span className="dbm-target-value">{config.targetLabel}</span>
          </div>

          {config.previewCounts && (
            <div className="dbm-preview-counts">
              {Object.values(config.previewCounts).map((c) => (
                <div key={c.displayName} className="dbm-preview-row">
                  <span>{c.displayName}</span>
                  <span className="dbm-count-badge">{c.count.toLocaleString()} records</span>
                </div>
              ))}
              <div className="dbm-preview-row total">
                <span>Total</span>
                <span className="dbm-count-badge danger">
                  {Object.values(config.previewCounts)
                    .reduce((s, c) => s + c.count, 0)
                    .toLocaleString()} records
                </span>
              </div>
            </div>
          )}

          {step === 1 && (
            <>
              <p className="dbm-modal-warning">
                Enter your admin login password to proceed with deletion.
              </p>
              <div className="dbm-field">
                <label>Admin Password</label>
                <input
                  type="password"
                  placeholder="Your login password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleStep1()}
                  autoFocus
                />
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="dbm-modal-warning danger-text">
                ⚠️ This action is <strong>permanent and cannot be undone</strong>. All records shown above will be deleted from the database.
              </p>
              <div className="dbm-field">
                <label>Type <code>DELETE</code> to confirm</label>
                <input
                  type="text"
                  placeholder="DELETE"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
                  autoFocus
                />
              </div>
            </>
          )}

          {error && <div className="dbm-error-msg">❌ {error}</div>}
        </div>

        <div className="dbm-modal-footer">
          <button className="dbm-btn secondary" onClick={() => onClose(null)} disabled={loading}>
            Cancel
          </button>
          {step === 1 && (
            <button className="dbm-btn danger" onClick={handleStep1}>
              Next →
            </button>
          )}
          {step === 2 && (
            <>
              <button className="dbm-btn secondary" onClick={() => { setStep(1); setConfirmText(''); setError(''); }}>
                ← Back
              </button>
              <button
                className="dbm-btn danger"
                onClick={handleConfirm}
                disabled={loading || confirmText.trim() !== 'DELETE'}
              >
                {loading ? 'Deleting...' : 'Confirm Delete'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Result Toast
───────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`dbm-toast ${type}`}>
      <span>{message}</span>
      <button onClick={onClose}>✕</button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   Reusable Section Panel
───────────────────────────────────────────── */
function DbSection({
  title,
  icon,
  colorClass,
  itemLabel = 'Collection',
  deleteScopeLabel = 'collections',
  dates,
  datesLoading,
  isIso,          // true = market ISO dates, false = strategy DD-MM-YY
  countByDateFn,
  countByMonthFn,
  deleteByDateFn,
  deleteByMonthFn,
  onResult,
}) {
  const [activeTab, setActiveTab]     = useState('view-date');
  const [selectedDate, setSelectedDate]   = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [datePreview, setDatePreview]     = useState(null);
  const [monthPreview, setMonthPreview]   = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [modal, setModal] = useState(null);

  // Build catalog once when dates load
  const { catalog, years } = useMemo(
    () => (dates.length ? buildDateCatalog(dates, isIso) : { catalog: {}, years: [] }),
    [dates, isIso]
  );

  // Reset selections when tab changes
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedDate(null);
    setSelectedMonth(null);
    setDatePreview(null);
    setMonthPreview(null);
  };

  const loadDatePreview = useCallback(async (date) => {
    if (!date) { setDatePreview(null); return; }
    setPreviewLoading(true);
    try {
      const { data } = await countByDateFn(date);
      setDatePreview(data);
    } catch { setDatePreview(null); }
    finally  { setPreviewLoading(false); }
  }, [countByDateFn]);

  const loadMonthPreview = useCallback(async (ym) => {
    if (!ym) { setMonthPreview(null); return; }
    setPreviewLoading(true);
    try {
      const { data } = await countByMonthFn(ym);
      setMonthPreview(data);
    } catch { setMonthPreview(null); }
    finally  { setPreviewLoading(false); }
  }, [countByMonthFn]);

  const handleDateSelect = (v) => { setSelectedDate(v); loadDatePreview(v); };
  const handleMonthSelect = (v) => { setSelectedMonth(v); loadMonthPreview(v); };

  const openDeleteByDate = () => {
    setModal({
      sectionLabel: title,
      targetLabel: `Date: ${humanDate(selectedDate, isIso)}`,
      previewCounts: datePreview?.collections,
      onConfirm: async (pwd) => { const { data } = await deleteByDateFn(selectedDate, pwd); return data; },
    });
  };

  const openDeleteByMonth = () => {
    setModal({
      sectionLabel: title,
      targetLabel: `Month: ${formatMonthLabel(selectedMonth)}`,
      previewCounts: monthPreview?.collections,
      onConfirm: async (pwd) => { const { data } = await deleteByMonthFn(selectedMonth, pwd); return data; },
    });
  };

  const handleModalClose = (result) => {
    setModal(null);
    if (result?.success) {
      onResult({ type: 'success', message: '✅ Records deleted successfully.' });
      setSelectedDate(null);
      setSelectedMonth(null);
      setDatePreview(null);
      setMonthPreview(null);
    }
  };

  const renderCountTable = (preview, label) => {
    if (!preview) return null;
    return (
      <div className="dbm-preview-table-wrap">
        <h4 className="dbm-preview-title">Records for — {label}</h4>
        <table className="dbm-preview-table">
          <thead><tr><th>{itemLabel}</th><th>Records</th></tr></thead>
          <tbody>
            {Object.values(preview.collections).map((c) => (
              <tr key={c.displayName}>
                <td>{c.displayName}</td>
                <td><span className="dbm-badge">{c.count.toLocaleString()}</span></td>
              </tr>
            ))}
            <tr className="dbm-total-row">
              <td><strong>Total</strong></td>
              <td><span className="dbm-badge total">{preview.totalRecords.toLocaleString()}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const dateLabel   = selectedDate  ? humanDate(selectedDate, isIso)       : '';
  const monthLabel  = selectedMonth ? formatMonthLabel(selectedMonth)       : '';

  return (
    <div className={`dbm-section ${colorClass}`}>
      <div className="dbm-section-header">
        <span className="dbm-section-icon">{icon}</span>
        <h2>{title}</h2>
        {datesLoading && <span className="dbm-loading-inline">Loading dates…</span>}
      </div>

      <div className="dbm-tabs">
        {[
          { id: 'view-date',    label: '📋 View by Date'    },
          { id: 'delete-date',  label: '🗑️ Delete by Date'  },
          { id: 'delete-month', label: '🗑️ Delete by Month' },
        ].map((tab) => (
          <button
            key={tab.id}
            className={`dbm-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── View by Date ── */}
      {activeTab === 'view-date' && (
        <div className="dbm-panel">
          <CascadingDatePicker catalog={catalog} years={years} onChange={handleDateSelect} />
          {previewLoading && <div className="dbm-loading">Loading counts…</div>}
          {renderCountTable(datePreview, dateLabel)}
          {selectedDate && !previewLoading && datePreview?.totalRecords === 0 && (
            <div className="dbm-empty">No records found for {dateLabel}.</div>
          )}
        </div>
      )}

      {/* ── Delete by Date ── */}
      {activeTab === 'delete-date' && (
        <div className="dbm-panel">
          <div className="dbm-warning-banner">
            ⚠️ Deletes ALL records for the selected date across all {deleteScopeLabel}. This cannot be undone.
          </div>
          <div className="dbm-cascade-row">
            <CascadingDatePicker catalog={catalog} years={years} onChange={handleDateSelect} />
            <div className="dbm-action-col">
              <button
                className="dbm-btn danger"
                disabled={!selectedDate || previewLoading || !datePreview || datePreview.totalRecords === 0}
                onClick={openDeleteByDate}
              >
                🗑️ Delete Date
              </button>
            </div>
          </div>
          {previewLoading && <div className="dbm-loading">Loading counts…</div>}
          {renderCountTable(datePreview, dateLabel)}
          {selectedDate && !previewLoading && datePreview?.totalRecords === 0 && (
            <div className="dbm-empty">No records found for {dateLabel}.</div>
          )}
        </div>
      )}

      {/* ── Delete by Month ── */}
      {activeTab === 'delete-month' && (
        <div className="dbm-panel">
          <div className="dbm-warning-banner">
            ⚠️ Deletes ALL records for the selected month across all {deleteScopeLabel}. This cannot be undone.
          </div>
          <div className="dbm-cascade-row">
            <CascadingMonthPicker catalog={catalog} years={years} onChange={handleMonthSelect} />
            <div className="dbm-action-col">
              <button
                className="dbm-btn danger"
                disabled={!selectedMonth || previewLoading || !monthPreview || monthPreview.totalRecords === 0}
                onClick={openDeleteByMonth}
              >
                🗑️ Delete Month
              </button>
            </div>
          </div>
          {previewLoading && <div className="dbm-loading">Loading counts…</div>}
          {renderCountTable(monthPreview, monthLabel)}
          {selectedMonth && !previewLoading && monthPreview?.totalRecords === 0 && (
            <div className="dbm-empty">No records found for {monthLabel}.</div>
          )}
        </div>
      )}

      {modal && <ConfirmModal config={modal} onClose={handleModalClose} />}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Main Page
───────────────────────────────────────────── */
const DBManagement = () => {
  const [marketDates,   setMarketDates]   = useState([]);
  const [strategyDates, setStrategyDates] = useState([]);
  const [marketLoading,   setMarketLoading]   = useState(true);
  const [strategyLoading, setStrategyLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    getMarketDates()
      .then(({ data }) => setMarketDates(data.allDates || []))
      .catch(() => setMarketDates([]))
      .finally(() => setMarketLoading(false));

    getStrategyDbDates()
      .then(({ data }) => setStrategyDates(data.dates || []))
      .catch(() => setStrategyDates([]))
      .finally(() => setStrategyLoading(false));
  }, []);

  const handleResult = (res) => setToast(res);

  return (
    <AppLayout pageBadge="🗄️ DB Management">
      <div className="dbm-root">

        {/* ── Top bar ── */}
        <div className="dbm-topbar">
          <span className="dbm-topbar-icon">🗄️</span>
          <span className="dbm-topbar-title">Database Management</span>
          <span className="dbm-topbar-sep" />
          <span className="dbm-topbar-desc">
            View &amp; manage market and strategy data · delete operations require admin password + double confirmation
          </span>
          <div className="dbm-topbar-chips">
            <span className="dbm-chip market">Market Data</span>
            <span className="dbm-chip strategy">Strategy Data</span>
          </div>
        </div>

        {/* ── Two-column body ── */}
        <div className="dbm-body">

          {/* Section 1 — Market Data */}
          <DbSection
            title="Market Data"
            icon="📊"
            colorClass="market"
            dates={marketDates}
            datesLoading={marketLoading}
            isIso={true}
            countByDateFn={marketCountByDate}
            countByMonthFn={marketCountByMonth}
            deleteByDateFn={marketDeleteByDate}
            deleteByMonthFn={marketDeleteByMonth}
            onResult={handleResult}
          />

          {/* Section 2 — Strategy Data */}
          <DbSection
            title="Strategy Data"
            icon="🎯"
            colorClass="strategy"
            itemLabel="Strategy"
            deleteScopeLabel="strategies in the unified StrategyData collection"
            dates={strategyDates}
            datesLoading={strategyLoading}
            isIso={false}
            countByDateFn={strategyCountByDate}
            countByMonthFn={strategyCountByMonth}
            deleteByDateFn={strategyDeleteByDate}
            deleteByMonthFn={strategyDeleteByMonth}
            onResult={handleResult}
          />
        </div>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default DBManagement;
